import { UseAxiosResult } from "axios-hooks";
import formatUrl from "utils/formatUrl";

import api from "config/urls/api";

import {
  GetClientId,
  PagedResponse,
  UseSelectUserManagementApi,
} from "./setup";

interface InvitationsLatestProps {
  page: number;
  pageSize?: number;
}

export interface CodatClientInvitation {
  codatClientId: string;
  email: string;
  inviteExpiresOn?: string;
  inviteAcceptedOn?: string;
  inviteDeclinedOn?: string;
  createdOn: string;
  updatedOn?: string;
  clientRole: string;
  userName?: string;
  requiresConsent: boolean;
}

export interface InvitationProps {
  clientInvitationId: string;
}

export interface FindInvitationProps {
  codatClientId: string;
  email: string;
}

export const useGetCodatClientInvitations = (
  clientId: string,
  queryParams: InvitationsLatestProps
): UseAxiosResult<PagedResponse<CodatClientInvitation>> => {
  return UseSelectUserManagementApi<PagedResponse<CodatClientInvitation>>({
    url: formatUrl(api.clientInvitations.all, { clientId }),
    method: "GET",
    params: queryParams,
  });
};

export const useGetCodatClientInvitation = ({
  clientInvitationId,
}: InvitationProps): UseAxiosResult<CodatClientInvitation> => {
  return UseSelectUserManagementApi<CodatClientInvitation>(
    {
      url: formatUrl(api.clientInvitations.one, { clientInvitationId }),
      method: "GET",
    },
    {
      manual: false,
    }
  );
};

export const useResendCodatClientInvitation = (
  email: string
): UseAxiosResult<CodatClientInvitation> => {
  const clientId = GetClientId();
  return UseSelectUserManagementApi<CodatClientInvitation>(
    {
      url: formatUrl(api.clientInvitations.resendInvite, {
        clientId,
      }),
      method: "PUT",
      params: { email },
    },
    {
      manual: true,
    }
  );
};

export const useAcceptCodatClientInvitation = ({
  clientInvitationId,
}: InvitationProps): UseAxiosResult<CodatClientInvitation> => {
  return UseSelectUserManagementApi<CodatClientInvitation>(
    {
      url: formatUrl(api.clientInvitations.acceptInvite, {
        clientInvitationId,
      }),
      method: "PUT",
    },
    {
      manual: true,
    }
  );
};

export const useDeclineCodatClientInvitation = ({
  clientInvitationId,
}: InvitationProps): UseAxiosResult<CodatClientInvitation> => {
  return UseSelectUserManagementApi<CodatClientInvitation>(
    {
      url: formatUrl(api.clientInvitations.declineInvite, {
        clientInvitationId,
      }),
      method: "PUT",
    },
    {
      manual: true,
    }
  );
};

export const useInviteUserToClient =
  (): UseAxiosResult<CodatClientInvitation> => {
    const clientId = GetClientId();
    return UseSelectUserManagementApi<CodatClientInvitation>(
      {
        url: formatUrl(api.clientInvitations.invite, { clientId }),
        method: "POST",
      },
      {
        manual: true,
      }
    );
  };

export const useDeleteClientInvitation = ({
  codatClientId,
  email,
}: FindInvitationProps): UseAxiosResult => {
  return UseSelectUserManagementApi(
    {
      url: formatUrl(api.clientInvitations.deleteInvite),
      method: "DELETE",
      params: {
        codatClientId: codatClientId,
        email: email,
      },
    },
    {
      manual: true,
    }
  );
};
